import { useCallback, useEffect, useState } from 'react';
import { ImagePlus, Link2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
} from '@/components/painel/painel-styles';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  buildChangelogFigureHtml,
  buildResizableImageHtml,
  type CmsRichImageVariant,
} from '@/lib/cms-rich-helpers';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

export type InsertImageDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onInsert: (html: string) => void;
  imageVariant: CmsRichImageVariant;
  changelogFigure?: boolean;
  contentClassName?: string;
};

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('read'));
    r.readAsDataURL(file);
  });
}

export function InsertImageDialog({
  open,
  onOpenChange,
  onInsert,
  imageVariant,
  changelogFigure = false,
}: InsertImageDialogProps) {
  const [tab, setTab] = useState('file');
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [fileLabel, setFileLabel] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [pendingDataUrl, setPendingDataUrl] = useState<string | null>(null);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setTab('file');
    setUrl('');
    setAlt('');
    setCaption('');
    setFileLabel('');
    setPreview(null);
    setPendingDataUrl(null);
    setError('');
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const onPickFile = (f: File | null) => {
    setError('');
    setPendingDataUrl(null);
    setPreview(null);
    if (!f) {
      setFileLabel('');
      return;
    }
    if (!f.type.startsWith('image/')) {
      setError('Escolha um ficheiro de imagem.');
      setFileLabel('');
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setError('Imagem demasiado grande (máx. 5 MB).');
      setFileLabel('');
      return;
    }
    setFileLabel(f.name);
    const objUrl = URL.createObjectURL(f);
    setPreview(objUrl);
    void readFileAsDataUrl(f)
      .then(setPendingDataUrl)
      .catch(() => setError('Não foi possível ler o ficheiro.'));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setTab('file');
      onPickFile(f);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const closeAndInsert = () => {
    setError('');
    let src = '';
    if (tab === 'file') {
      src = pendingDataUrl ?? '';
      if (!src) {
        setError('Selecione uma imagem ou use o separador URL.');
        return;
      }
    } else {
      src = url.trim();
      if (!src) {
        setError('Indique um URL válido.');
        return;
      }
    }

    const altText = alt.trim() || 'Imagem';
    const html = changelogFigure
      ? buildChangelogFigureHtml(src, altText, caption.trim() || undefined, imageVariant)
      : buildResizableImageHtml(src, altText, imageVariant);

    onInsert(html);
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-lg overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">Inserir imagem</DialogTitle>
        <PainelPermissionsDialogShell>
          <div className="shrink-0 border-b border-white/10 bg-violet-950/20 px-6 pb-5 pt-6 sm:px-7">
            <div className="flex items-start gap-4 pr-12">
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/25 to-violet-700/10 ring-1 ring-violet-400/35 shadow-[0_0_40px_-10px_rgba(139,92,246,0.35)]">
                <ImagePlus className="h-6 w-6 text-violet-200" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300/55">
                  Editor
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white">Inserir imagem</h2>
                <p className="mt-1.5 font-inter text-sm leading-relaxed text-slate-400">
                  Envie um ficheiro ou cole um URL. Depois pode redimensionar no editor.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 px-6 py-5 sm:px-7">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 border border-white/10 bg-white/[0.04]">
                <TabsTrigger
                  value="file"
                  className="gap-2 data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-100"
                >
                  <Upload className="h-4 w-4" />
                  Ficheiro
                </TabsTrigger>
                <TabsTrigger
                  value="url"
                  className="gap-2 data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-100"
                >
                  <Link2 className="h-4 w-4" />
                  URL
                </TabsTrigger>
              </TabsList>
              <TabsContent value="file" className="mt-4 space-y-3">
                <div
                  className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.03] px-4 py-8 transition hover:border-violet-400/40 hover:bg-violet-500/[0.06]"
                  onClick={() => document.getElementById('cms-insert-img-file')?.click()}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && document.getElementById('cms-insert-img-file')?.click()
                  }
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  role="button"
                  tabIndex={0}
                >
                  <ImagePlus className="mb-2 h-10 w-10 text-violet-300/80" />
                  <span className="font-inter text-sm font-medium text-slate-200">
                    Clique ou largue uma imagem
                  </span>
                  {fileLabel ? (
                    <span className="mt-2 font-mono text-xs text-slate-500">{fileLabel}</span>
                  ) : null}
                </div>
                <input
                  id="cms-insert-img-file"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                />
                {preview ? (
                  <img
                    src={pendingDataUrl ?? preview}
                    alt=""
                    className="mx-auto max-h-48 rounded-xl border border-white/10 object-contain"
                  />
                ) : null}
              </TabsContent>
              <TabsContent value="url" className="mt-4 space-y-2">
                <Label className="font-poppins text-slate-300">URL da imagem</Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://…"
                  className={painelInputClass}
                />
              </TabsContent>
            </Tabs>

            <div className="space-y-2">
              <Label className="font-poppins text-slate-300">Texto alternativo</Label>
              <Input
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                placeholder="Descrição para acessibilidade"
                className={painelInputClass}
              />
            </div>

            {changelogFigure ? (
              <div className="space-y-2">
                <Label className="font-poppins text-slate-300">Legenda (opcional)</Label>
                <Input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Texto abaixo da imagem"
                  className={painelInputClass}
                />
              </div>
            ) : null}

            {error ? (
              <p className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 font-inter text-xs text-rose-200">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-7">
            <Button
              type="button"
              variant="ghost"
              className={painelModalCancelButtonClass}
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              onClick={closeAndInsert}
            >
              Inserir imagem
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
